import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useMassMigrationBatches } from '../hooks/useMassMigrationBatches';
import type { MassMigrationBatchStatus, MassMigrationBatchSummaryDto } from '@/types/massMigration';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimeAgo(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const safeDiff = Math.max(0, now - then);
  const diffSec = Math.floor(safeDiff / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  return `${diffDays}d ago`;
}

type FilterPill = 'All' | 'Running' | 'Issues' | 'Succeeded';

const STATUS_CONFIG: Record<
  MassMigrationBatchStatus,
  { label: string; variant: 'info' | 'success' | 'destructive' | 'warning' | 'secondary' | 'outline' }
> = {
  InProgress: { label: 'In Progress', variant: 'info' },
  Succeeded: { label: 'Succeeded', variant: 'success' },
  Failed: { label: 'Failed', variant: 'destructive' },
  Partial: { label: 'Partial', variant: 'warning' },
  Pending: { label: 'Pending', variant: 'secondary' },
  Cancelled: { label: 'Cancelled', variant: 'outline' },
};

function BatchStatusBadge({
  status,
  pulsing,
}: {
  status: MassMigrationBatchStatus;
  pulsing?: boolean | undefined;
}) {
  const config = STATUS_CONFIG[status] ?? { label: status, variant: 'secondary' as const };
  return (
    <div className="inline-flex items-center gap-1.5">
      {pulsing && (
        <span
          className="inline-block h-2 w-2 rounded-full bg-info animate-pulse"
          aria-hidden="true"
        />
      )}
      <Badge variant={config.variant}>{config.label}</Badge>
    </div>
  );
}

// ─── HistoryRow ───────────────────────────────────────────────────────────────

interface HistoryRowProps {
  index: number;
  batch: MassMigrationBatchSummaryDto;
  onClick: () => void;
}

function HistoryRow({ index, batch, onClick }: HistoryRowProps) {
  return (
    <tr
      key={batch.id}
      className={cn(
        'border-b border-border transition-colors cursor-pointer',
        'hover:bg-muted/50 focus-within:bg-muted/50',
      )}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Open batch ${batch.name}`}
    >
      <td className="px-4 py-3 text-sm text-muted-foreground">#{index + 1}</td>
      <td className="px-4 py-3">
        <div className="font-medium text-sm">{batch.name}</div>
        <div className="text-xs text-muted-foreground font-mono">{batch.id.slice(0, 8)}…</div>
      </td>
      <td className="px-4 py-3">
        <BatchStatusBadge status={batch.status} pulsing={batch.status === 'InProgress'} />
      </td>
      <td className="px-4 py-3 text-sm">
        <span className="text-foreground">{batch.totalOrgs} orgs</span>
        {batch.partial > 0 && (
          <span className="ml-2 text-warning font-medium">{batch.partial} partial</span>
        )}
        {batch.failed > 0 && (
          <span className="ml-2 text-destructive font-medium">{batch.failed} failed</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">{formatTimeAgo(batch.createdAt)}</td>
    </tr>
  );
}

// ─── SkeletonRows ─────────────────────────────────────────────────────────────

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="border-b border-border">
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-6" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-40 mb-1" />
            <Skeleton className="h-3 w-20" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-5 w-20 rounded-full" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-24" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-16" />
          </td>
        </tr>
      ))}
    </>
  );
}

// ─── BatchHistoryTab ──────────────────────────────────────────────────────────

export function BatchHistoryTab() {
  const navigate = useNavigate();
  const { data: batches, isLoading, isError, error } = useMassMigrationBatches();

  const [activeFilter, setActiveFilter] = useState<FilterPill>('All');
  const [search, setSearch] = useState('');

  const runningBatches = (batches ?? []).filter((b) => b.status === 'InProgress');

  const filtered = (batches ?? []).filter((b) => {
    const matchesFilter =
      activeFilter === 'All' ||
      (activeFilter === 'Running' && b.status === 'InProgress') ||
      (activeFilter === 'Issues' && (b.status === 'Failed' || b.status === 'Partial')) ||
      (activeFilter === 'Succeeded' && b.status === 'Succeeded');
    const matchesSearch = search === '' || b.name.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const filterPills: FilterPill[] = ['All', 'Running', 'Issues', 'Succeeded'];

  function handleRowClick(batchId: string) {
    void navigate({ to: '/migrations/batches/$batchId', params: { batchId } });
  }

  return (
    <div className="space-y-4">
      {/* Live banner */}
      {runningBatches.length > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-info/40 bg-info/10 px-4 py-3">
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full bg-info animate-pulse"
              aria-hidden="true"
            />
            <span className="text-sm font-medium">
              {runningBatches.length === 1
                ? '1 batch running'
                : `${runningBatches.length} batches running`}
            </span>
          </div>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-info"
            onClick={() => {
              const latestRunning = [...runningBatches].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
              )[0]!;
              handleRowClick(latestRunning.id);
            }}
          >
            {runningBatches.length === 1 ? 'Open live view →' : 'Open latest live view →'}
          </Button>
        </div>
      )}

      {/* Filters + Search */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1" role="group" aria-label="Filter batches">
          {filterPills.map((pill) => (
            <button
              key={pill}
              type="button"
              onClick={() => setActiveFilter(pill)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                activeFilter === pill
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80',
              )}
              aria-pressed={activeFilter === pill}
            >
              {pill}
            </button>
          ))}
        </div>
        <div className="ml-auto">
          <Input
            placeholder="Search batches..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-48 text-sm"
            aria-label="Search batches by name"
          />
        </div>
      </div>

      {/* Error state */}
      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to load batches</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : 'An unexpected error occurred.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Table */}
      <div className="rounded-md border border-border overflow-hidden">
        <table className="w-full text-sm">
          <caption className="sr-only">Migration batch history</caption>
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">#</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                Batch
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                Status
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                Orgs
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                Created
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows />
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {batches?.length === 0
                    ? 'No batches yet. Start a mass migration to create one.'
                    : 'No batches match your filters.'}
                </td>
              </tr>
            ) : (
              filtered.map((batch, i) => (
                <HistoryRow
                  key={batch.id}
                  index={i}
                  batch={batch}
                  onClick={() => handleRowClick(batch.id)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
